/**
 * Swap订单列表组件
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useFm } from '@web3/base-hooks';
import { formatLocalTime } from '@/utils/util';
import { OrderDetail } from './OrderDetail';

import TokenIcon from '@/components/TokenIcon';
import OrderId from '@/components/OrderId';
import OrderStatus from '@/components/OrderStatus';
import { ArrowchevronDownIcon } from '@bfe/moly-icons';
import Amount from '@/components/Amount';
import Empty from '@/components/Empty';
import { SwapOrder } from '@/services/swap';
import { useIntersection } from 'react-use';
import { Spin } from '@bfe/moly';

interface Props {
  loading?: boolean;
  data: SwapOrder[];
  hasMore?: boolean;
  loadMore?: () => void;
}

export default function OrderList({ loading, data, hasMore, loadMore }: Props) {
  const t = useFm();
  const [childrenVisibleIndex, setChildrenVisibleIndex] = useState<number>(-1);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isFirst = useRef(true);

  const handleShowDetail = useCallback(
    (index: number) => {
      if (childrenVisibleIndex !== index) {
        setChildrenVisibleIndex(index);
      } else {
        setChildrenVisibleIndex(-1);
      }
    },
    [childrenVisibleIndex]
  );

  const intersection = useIntersection(loadMoreRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });

  useEffect(() => {
    if (!data?.length) {
      isFirst.current = true;
    }
  }, [data]);

  useEffect(() => {
    console.log('load more', intersection);
    if (intersection && intersection.isIntersecting && hasMore) {
      if (isFirst.current) {
        isFirst.current = false;
        return;
      }
      loadMore();
    }
  }, [intersection, loadMore]);

  return (
    <table cellSpacing="0" border={0} cellPadding="0" className="w-full">
      <thead>
        <tr className="text-left text-bds-gray-t2 text-xs bg-bds-gray-ele-edge">
          <th className="p-3">{t('pairs')}</th>
          <th className="p-3">{t('pay')}</th>
          <th className="p-3">{t('receive')}</th>
          <th className="p-3">{t('swapFee')}</th>
          <th className="p-3">{t('orderId')}</th>
          <th className="p-3">{t('status')}</th>
          <th className="p-3 w-[80px]">{t('action')}</th>
        </tr>
      </thead>
      <tbody>
        {!!data?.length &&
          data.map((row, index) => (
            <Fragment key={row.id}>
              <tr
                key={row.id}
                className="text-sm font-medium border-b border-bds-gray-ele-line"
              >
                <td className="p-3">
                  <div className="inline-flex flex-col gap-0.5">
                    <p className="text-base font-medium">
                      {row.fromToken?.symbol ?? '--'}/
                      {row.toToken?.symbol ?? '--'}
                    </p>
                    <p className="text-sm text-bds-gray-t2">
                      {formatLocalTime(+row.createdAt * 1000)}
                    </p>
                  </div>
                </td>
                <td className="p-3">
                  <div className="inline-flex items-center gap-1">
                    <TokenIcon
                      tokenIcon={row.fromToken?.iconUri}
                      chainIcon={row.fromChainIconUrl}
                    />
                    <Amount
                      amount={row.fromTokenAmount}
                      symbol={row.fromToken?.symbol}
                    />
                  </div>
                </td>
                <td className="p-3">
                  <div className="inline-flex items-center gap-1">
                    <TokenIcon
                      tokenIcon={row.toToken.iconUri}
                      chainIcon={row.toChainIconUrl}
                    />
                    <Amount
                      amount={row.toTokenAmount}
                      symbol={row.toToken.symbol}
                    />
                  </div>
                </td>
                <td className="p-3">
                  <Amount
                    amount={row.feeAmount}
                    symbol={row.fromToken?.symbol}
                  />
                </td>
                <td className="p-3">
                  <OrderId orderId={row.orderId} />
                </td>
                <td className="p-3">
                  <OrderStatus status={row.status} />
                </td>
                <td className="p-3 w-[80px]">
                  <p
                    className="cursor-pointer text-[#9747ff] inline-flex items-center gap-1"
                    onClick={() => handleShowDetail(index)}
                  >
                    <span>{t('details')} </span>
                    <ArrowchevronDownIcon
                      className={`size-4 ${childrenVisibleIndex === index && 'rotate-180'}`}
                    />
                  </p>
                </td>
              </tr>
              {childrenVisibleIndex === index && (
                <tr key={`${row.id}_detail`}>
                  <td colSpan={7}>
                    <OrderDetail data={row.orderTransactions} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        <tr>
          <td colSpan={7}>
            <div ref={loadMoreRef}></div>
          </td>
        </tr>
        {loading && (
          <tr>
            <td colSpan={7} className="h-[138px] text-center">
              <Spin />
            </td>
          </tr>
        )}
        {!data?.length && !loading && (
          <tr>
            <td colSpan={7}>
              <Empty />
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
